import { useEffect, useState } from "react";
import { Check, Copy, Link2, Loader2, RefreshCw, ShieldOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function GestioneLinkBolle() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [copiato, setCopiato] = useState("");
  const [indirizzo, setIndirizzo] = useState(() => localStorage.getItem("bolleBaseUrl") || window.location.origin);

  const carica = async () => {
    setLoading(true);
    try {
      const risposta = await api.get("/bolle-links");
      setItems(Array.isArray(risposta.data) ? risposta.data : []);
    } catch (errore) {
      toast.error(formatApiError(errore.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { carica(); }, []);

  const baseUrl = indirizzo.trim().replace(/\/$/, "") || window.location.origin;
  const linkCompleto = (token) => `${baseUrl}/bolle/${token}`;

  const genera = async (item) => {
    const messaggio = item.token
      ? `Rigenerare il link di ${item.operaio_nome}? Il link precedente smetterà immediatamente di funzionare.`
      : `Creare il link personale per ${item.operaio_nome}?`;
    if (!window.confirm(messaggio)) return;
    setBusy(item.operaio_id);
    try {
      await api.post(`/bolle-links/${item.operaio_id}`);
      toast.success(item.token ? "Nuovo link generato" : "Link creato");
      await carica();
    } catch (errore) { toast.error(formatApiError(errore.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const cambiaStato = async (item) => {
    setBusy(item.operaio_id);
    try {
      await api.put(`/bolle-links/${item.operaio_id}/stato`, null, { params: { attivo: !item.attivo } });
      toast.success(item.attivo ? "Link disattivato" : "Link riattivato");
      await carica();
    } catch (errore) { toast.error(formatApiError(errore.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const copia = async (item) => {
    await navigator.clipboard.writeText(linkCompleto(item.token));
    setCopiato(item.operaio_id);
    toast.success("Link copiato: puoi inviarlo tramite WhatsApp");
    window.setTimeout(() => setCopiato(""), 1800);
  };

  return <div className="max-w-5xl space-y-6">
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Smartphone className="h-6 w-6" /></div>
        <div><h2 className="font-heading text-2xl font-bold">Web app Bolle operai</h2><p className="text-muted-foreground mt-1">Crea un link personale per ogni operaio. Il link consente soltanto di fotografare e inviare una bolla, senza accedere al gestionale.</p></div>
      </div>
      <div className="mt-5 border-t pt-5"><label className="text-sm font-semibold">Indirizzo usato nei link</label><div className="mt-2 flex flex-col sm:flex-row gap-2"><Input value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)} placeholder="http://192.168.1.10:8001"/><Button variant="outline" onClick={() => { localStorage.setItem("bolleBaseUrl", baseUrl); toast.success("Indirizzo salvato"); }}>Salva indirizzo</Button></div><p className="mt-2 text-xs text-muted-foreground">Sul localhost inserisci l’indirizzo di rete del PC, per esempio http://192.168.1.10:8001. Online lascia il dominio proposto.</p></div>
    </div>

    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[850px] text-sm">
          <thead className="bg-muted/70"><tr>{["Operaio", "Stato", "Link personale", "Azioni"].map((titolo) => <th key={titolo} className="p-4 text-left text-xs uppercase tracking-wider text-muted-foreground">{titolo}</th>)}</tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="4" className="p-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Caricamento...</td></tr>
              : items.length === 0 ? <tr><td colSpan="4" className="p-10 text-center text-muted-foreground">Nessun operaio attivo presente.</td></tr>
              : items.map((item) => <tr key={item.operaio_id} className="border-t">
                <td className="p-4 font-semibold">{item.operaio_nome}</td>
                <td className="p-4"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${item.attivo ? "bg-emerald-100 text-emerald-700" : item.token ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>{item.attivo ? "Attivo" : item.token ? "Disattivato" : "Da creare"}</span></td>
                <td className="p-4 max-w-[330px]">{item.token ? <span className="block truncate font-mono text-xs text-muted-foreground">{linkCompleto(item.token)}</span> : "—"}</td>
                <td className="p-4"><div className="flex flex-wrap gap-2">
                  {!item.token ? <Button size="sm" onClick={() => genera(item)} disabled={busy === item.operaio_id}><Link2 className="h-4 w-4 mr-1" />Crea link</Button> : <>
                    <Button size="sm" variant="outline" onClick={() => copia(item)} disabled={!item.attivo}>{copiato === item.operaio_id ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}Copia</Button>
                    <Button size="sm" variant="outline" onClick={() => genera(item)} disabled={busy === item.operaio_id}><RefreshCw className="h-4 w-4 mr-1" />Rigenera</Button>
                    <Button size="sm" variant="ghost" onClick={() => cambiaStato(item)} disabled={busy === item.operaio_id} className={item.attivo ? "text-destructive" : "text-emerald-700"}><ShieldOff className="h-4 w-4 mr-1" />{item.attivo ? "Disattiva" : "Riattiva"}</Button>
                  </>}
                </div></td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>
    <p className="text-sm text-muted-foreground">Il telefono e il PC devono essere collegati alla stessa rete Wi-Fi quando usi il localhost.</p>
  </div>;
}
